import { dateFormatter } from '../../utils/DateFormatter'
import { FilenameTemplateItem } from './classes/FilenameTemplateItem'

/**
 * sets the help text and noRecordsFallback function for date pdf filename template items
 *
 * @param options {@link DateOptionsOptions}
 * @see {@link help}
 * @see {@link defaultsTo}
 */
export function dateOptions<
  RecordType extends object,
  ClassType extends string,
  Property extends keyof RecordType & string,
  IDType extends Lowercase<string>
>(
  options: DateOptionsOptions
): Pick<
  FilenameTemplateItem<RecordType, ClassType, Property, IDType>,
  'help' | 'exampleValue'
> {
  return {
    help: help(options),
    exampleValue: () => dateFormatter(null).file(new Date()),
  }
}

function help({ name, tableNameSingular }: DateOptionsOptions) {
  const defaults = defaultsTo({ name, property: 'Created Date' })
  return `${name} from ${tableNameSingular} table. ${defaults}. Formatted as YYYYMMDD` as const
}

type DateOptionsOptions = {
  /**
   * should match the name used in the constructor
   */
  name: string
  /**
   * @example 'Asphalt Lot'
   */
  tableNameSingular: string
}

export function defaultsTo<Name extends string, Property extends string>({
  name,
  property,
}: DefaultsToOptions<Name, Property>) {
  return `Defaults to ${property} if no ${name} is set` as const
}

type DefaultsToOptions<Name, Property> = {
  /**
   * the name of the property that will be "overwritten"
   */
  name: Name
  /**
   * the human readable name of the property that's the default
   *
   * @example 'Created Date'
   */
  property: Property
}
